/**
 * Card Subscription Box: "For Me" / "A Gift" purchase-type toggle.
 *
 * For Me: locks Term to the recurring option (identified structurally as
 *   the one Term value that doesn't start with a number - all the boxed
 *   terms do, e.g. "2 months") and hides the Term fieldset entirely. Also
 *   hides the Subscriptions widget's own UI entirely (there's nothing
 *   left for a shopper to choose - it's locked to Monthly automatically)
 *   while forcing its internal state into "subscribe" mode behind the
 *   scenes - clicking its Subscribe tab so the correct selling_plan ends
 *   up selected and enabled, since that hidden input is what actually
 *   gets submitted with the order. Clicking (not just setting .checked)
 *   matters here: the app's own script only clears the selling-plan
 *   radio's `disabled` attribute in response to a real interaction with
 *   the Subscribe tab, confirmed by comparing the widget's rendered HTML
 *   before vs. after a manual click - so this replicates that exact
 *   interaction rather than trying to reverse-engineer the app's
 *   internal state.
 * A Gift: hides the recurring Term option from the picker (replacing its
 *   remaining pill/swatch buttons with a dropdown - see
 *   renderTermDropdown()), ensures a boxed term is selected, hides the
 *   Subscriptions widget entirely, and disables its hidden selling_plan
 *   input so no subscription is submitted.
 *
 * The Subscriptions app pre-renders one <section data-variant-id="...">
 * per variant inside the widget container (toggling which one is visible
 * itself, independent of the theme) rather than fetching them on demand,
 * so the One-time-purchase-tab-hiding/Subscribe-tab-click above is
 * applied to every such section up front, plus to any later added via a
 * MutationObserver, rather than only the currently-visible one.
 *
 * Re-applies on every theme.pubsub variantChange event, not just once on
 * load, because the theme's <product-info> fully replaces the variant
 * picker's inner HTML on every option change (it re-fetches the section
 * via the Section Rendering API) - any direct DOM change made here would
 * otherwise be wiped out the next time a shopper changes Card Quantity,
 * Stamps, or Term.
 *
 * The Subscriptions app's own script is a black box we can't read source
 * for. It doesn't only add/remove DOM nodes - it also assigns element
 * IDs, toggles classes (its per-variant --hidden class) and flips the
 * disabled attribute asynchronously after its own init/network calls, on
 * a timeline we can't fully predict, and it may rebuild its whole widget
 * container from scratch on a variant change rather than mutating it in
 * place (which would orphan a MutationObserver attached to the old node).
 * Reacting to specific events (pubsub variantChange, native
 * variant:change, a MutationObserver) makes forceSubscribeOnly() run
 * *sooner*, which matters for avoiding a visible flash of the One-time
 * purchase tab, but none of them can be trusted alone to guarantee it
 * eventually runs - so a persistent interval re-applies state for as
 * long as this element is connected, for as long as it takes the app to
 * settle. applyState()/forceSubscribeOnly() are both idempotent/cheap
 * (a few querySelector calls once already converged), so polling
 * indefinitely costs nothing once things are in the right state.
 */
(function () {
  'use strict';

  function isRecurringInput(input) {
    return Number.isNaN(parseInt(input.value, 10));
  }

  const POLL_INTERVAL_MS = 400;

  // Diagnostic logging for this integration - several rounds of fixes were
  // needed to get it working, based on reading the code plus captures of
  // exactly this output, so it's left in rather than deleted outright.
  // Silent by default (this fires every poll tick - real shoppers don't
  // need that in their console); append ?subscription-debug to the page
  // URL to turn it back on. Printed as a JSON string rather than a live
  // object so a plain copy/paste of the console line carries the full
  // nested detail instead of a collapsed "Array(5), …" preview that needs
  // manual expanding in DevTools. Look for "[subscription-toggle]" in the
  // browser console.
  const DEBUG = /[?&]subscription-debug\b/.test(window.location.search);

  function logState(isGift, variantPickerFound, fieldset, termInputs, widget, sections) {
    if (!DEBUG) return;

    console.log('[subscription-toggle] ' + JSON.stringify({
      mode: isGift ? 'gift' : 'for-me',
      variantPickerFound,
      fieldsetFound: !!fieldset,
      fieldsetDisplay: fieldset ? fieldset.style.display : null,
      termInputs: termInputs.map((input) => ({
        value: input.value,
        checked: input.checked,
        recurring: isRecurringInput(input)
      })),
      widgetFound: !!widget,
      sectionCount: sections.length,
      sections: sections.map((section) => {
        const subscribeTab = section.querySelector('input.tab_radio[id*="tab_subscribe_"]');
        const sellingPlan = section.querySelector('input[data-radio-type="selling_plan"]');
        const otpTab = section.querySelector('input.tab_radio[id*="tab_otp_"]');
        const otpLabel = otpTab ? section.querySelector('label.tab_label[for="' + otpTab.id + '"]') : null;
        return {
          variantId: section.getAttribute('data-variant-id'),
          active: !section.classList.contains('shopify_subscriptions_app_block--hidden'),
          subscribeTabFound: !!subscribeTab,
          subscribeTabChecked: subscribeTab ? subscribeTab.checked : null,
          sellingPlanFound: !!sellingPlan,
          sellingPlanDisabled: sellingPlan ? sellingPlan.disabled : null,
          sellingPlanChecked: sellingPlan ? sellingPlan.checked : null,
          otpLabelFound: !!otpLabel,
          otpLabelHidden: otpLabel ? otpLabel.style.display === 'none' : null
        };
      })
    }));
  }

  class SubscriptionGiftToggle extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.productId = this.getAttribute('data-product-id');
      this.formId = this.getAttribute('data-form-id');

      this.toggleInputs = Array.from(this.querySelectorAll('input[data-purchase-type]'));
      this.giftFields = this.querySelector('[data-gift-fields]');
      this.recipientInput = this.querySelector('[data-recipient-name-input]');
      // Address 2 is intentionally excluded - it's the one optional
      // field in the recipient address, so it only ever needs clearing
      // on switch-back, never a `required` toggle.
      this.recipientAddressInputs = Array.from(this.querySelectorAll(
        '[data-recipient-address1-input], [data-recipient-city-input], [data-recipient-province-input], [data-recipient-zip-input], [data-recipient-country-input]'
      ));
      this.recipientAddress2Input = this.querySelector('[data-recipient-address2-input]');
      this.giftMessageInput = this.querySelector('[data-gift-message-input]');
      this.recurringNote = this.querySelector('[data-recurring-note]');
      this.nonRecurringNote = this.querySelector('[data-non-recurring-note]');

      // The gift fields get moved into <variant-picker> (see
      // applyGiftFields()) so they render below Term - stop their own
      // change events from bubbling up to it, same reason as the Term
      // dropdown's stopPropagation() below: <variant-picker> reacts to
      // ANY change event from ANY descendant as a possible variant
      // change, and while these particular inputs don't match its
      // option-aggregating selectors the way a <select> did, there's no
      // reason to let a Recipient Name blur trigger a pointless
      // section refetch either.
      if (this.giftFields) {
        this.giftFields.addEventListener('change', (event) => event.stopPropagation());
      }

      this._onToggleChange = this.applyState.bind(this);
      this.toggleInputs.forEach((input) => {
        input.addEventListener('change', this._onToggleChange);
      });

      if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
        this._unsubscribeVariantChange = window.theme.pubsub.subscribe(
          window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
          () => this.applyState()
        );
      }

      // The theme also dispatches a native "variant:change" CustomEvent
      // (on the product form, bubbling to document) right after the
      // pubsub event above. Listening for both, not just the pubsub one,
      // covers the case where the Subscriptions app's own reaction to a
      // variant change is tied to this one instead.
      this._onNativeVariantChange = () => this.applyState();
      document.addEventListener('variant:change', this._onNativeVariantChange);

      this.observeSubscriptionWidget();
      this.applyState();

      // The actual guarantee: whatever the app's own timing turns out to
      // be, this keeps re-asserting our state until it sticks.
      this._pollTimer = setInterval(() => this.applyState(), POLL_INTERVAL_MS);
    }

    disconnectedCallback() {
      this.toggleInputs.forEach((input) => {
        input.removeEventListener('change', this._onToggleChange);
      });
      if (typeof this._unsubscribeVariantChange === 'function') {
        this._unsubscribeVariantChange();
      }
      document.removeEventListener('variant:change', this._onNativeVariantChange);
      if (this._widgetObserver) {
        this._widgetObserver.disconnect();
      }
      clearInterval(this._pollTimer);
    }

    // The Subscriptions app may render its per-variant sections lazily,
    // may mutate existing ones in place (assigning IDs, toggling its
    // --hidden class, clearing disabled) rather than only adding/removing
    // nodes, or may replace its whole container outright on a variant
    // change. So this re-attaches whenever the live widget node differs
    // from the one already being observed, rather than assuming the
    // first node it finds is the one that matters forever - an observer
    // left attached to a node the app has since discarded would silently
    // stop firing for anything that happens afterward.
    observeSubscriptionWidget() {
      const widget = this.subscriptionWidget;
      if (!widget || widget === this._observedWidget) return;

      if (this._widgetObserver) this._widgetObserver.disconnect();

      this._observedWidget = widget;
      this._widgetObserver = new MutationObserver(() => {
        if (this.mode !== 'gift') this.forceSubscribeOnly(widget);
      });
      this._widgetObserver.observe(widget, { childList: true, subtree: true, attributes: true });
    }

    get mode() {
      const checked = this.toggleInputs.find((input) => input.checked);
      return checked ? checked.value : 'for-me';
    }

    get variantPicker() {
      return document.getElementById('VariantPicker-' + this.sectionId + '-' + this.productId);
    }

    get termFieldset() {
      const picker = this.variantPicker;
      return picker ? picker.querySelector('fieldset[data-option-slug="term"]') : null;
    }

    get subscriptionWidget() {
      return document.querySelector('.shopify_subscriptions_app_container');
    }

    // Buy Buttons is its own block, separate from Variant Picker, so
    // unlike the Term fieldset it isn't replaced by the theme's
    // section-rendering-API swap - found via the Add to cart button's
    // own form="" attribute rather than a fixed id/class, since
    // buy-buttons.liquid doesn't expose one of its own.
    get addToCartWrapper() {
      if (!this.formId) return null;
      const button = document.querySelector('button[name="add"][form="' + this.formId + '"]');
      return button ? button.closest('.product-form-wrapper') : null;
    }

    applyState() {
      const isGift = this.mode === 'gift';

      this.observeSubscriptionWidget();
      this.applyTermFieldset(isGift);
      this.applyGiftFields(isGift);
      this.applySubscriptionWidget(isGift);
      this.applyRecurringNote(isGift);
      this.applyNonRecurringNote(isGift);

      const fieldset = this.termFieldset;
      const termInputs = fieldset ? Array.from(fieldset.querySelectorAll('input[type="radio"]')) : [];
      const widget = this.subscriptionWidget;
      const sections = widget ? Array.from(widget.querySelectorAll('.shopify_subscriptions_app_block')) : [];
      logState(isGift, !!this.variantPicker, fieldset, termInputs, widget, sections);
    }

    applyTermFieldset(isGift) {
      const fieldset = this.termFieldset;
      if (!fieldset) return;

      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;

      const recurringInput = inputs.find(isRecurringInput);
      const boxedInputs = inputs.filter((input) => !isRecurringInput(input));

      if (!isGift) {
        fieldset.style.display = 'none';
        if (recurringInput && !recurringInput.checked) {
          recurringInput.checked = true;
          recurringInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      fieldset.style.display = '';

      if (recurringInput) {
        const wrapper = recurringInput.closest('li') || recurringInput.closest('magnet-element');
        if (wrapper) wrapper.style.display = 'none';
      }

      if (recurringInput && recurringInput.checked && boxedInputs.length) {
        boxedInputs[0].checked = true;
        boxedInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.renderTermDropdown(fieldset, boxedInputs);
    }

    // A Gift only ever offers the boxed Term options (the recurring one
    // is hidden above), and a dropdown reads better than a row of pill
    // swatches for a 4-way choice this deep in the form. Built here in
    // JS, not as Liquid markup, for the same reason the rest of this
    // fieldset's state is: the theme replaces the Term fieldset's whole
    // inner HTML on every option change, so anything rendered
    // server-side into it would just get wiped out the next time a
    // shopper changes Card Quantity or Stamps. Rebuilding/reusing it
    // fresh from the current boxedInputs every call, keyed to this
    // exact fieldset instance (a brand new one after every such swap),
    // keeps it correct without needing to persist anything across those
    // swaps itself. Selecting a dropdown option clicks the matching
    // (hidden) radio rather than driving the variant change directly,
    // so it goes through the exact same theme pipeline a real swatch
    // click would.
    renderTermDropdown(fieldset, boxedInputs) {
      const swatchList = fieldset.querySelector('ul.swatches');
      if (swatchList) swatchList.style.display = 'none';

      let wrapper = fieldset.querySelector('[data-term-dropdown-wrapper]');
      let dropdown;

      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'field variant-wrapper';
        wrapper.setAttribute('data-term-dropdown-wrapper', '');

        dropdown = document.createElement('select', { is: 'custom-select' });
        dropdown.setAttribute('is', 'custom-select');
        dropdown.className = 'select';
        dropdown.setAttribute('data-term-dropdown', '');
        const legend = fieldset.querySelector('legend');
        if (legend) dropdown.setAttribute('aria-label', legend.textContent);
        dropdown.addEventListener('change', (event) => {
          // The theme's <variant-picker> treats ANY <select> found
          // anywhere inside it as its own independent variant option
          // (VariantPicker.syncCurrentOptions matches 'select, fieldset'
          // indiscriminately) - since this dropdown lives nested inside
          // the real Term <fieldset>, its own native change event
          // bubbling up to that ancestor listener gets double-counted
          // as a phantom 4th option alongside Card Quantity/Stamps/Term,
          // corrupting the option_values sent to the server and
          // reverting the selection right back to the old value.
          // Stopped here since the actual Term change is already driven
          // correctly by clicking the real (matching) radio below - that
          // radio's own change event is what <variant-picker> should
          // see.
          event.stopPropagation();

          const match = boxedInputs.find((input) => input.value === dropdown.value);
          const checkedBefore = match ? match.checked : null;
          if (match && !match.checked) match.click();

          if (DEBUG) {
            console.log('[subscription-toggle] term dropdown change ' + JSON.stringify({
              dropdownValue: dropdown.value,
              optionValues: boxedInputs.map((input) => input.value),
              matchFound: !!match,
              checkedBefore,
              checkedAfterClick: match ? match.checked : null
            }));
          }
        });
        wrapper.appendChild(dropdown);

        const chevronTemplate = this.querySelector('[data-term-dropdown-chevron]');
        if (chevronTemplate) wrapper.appendChild(chevronTemplate.content.cloneNode(true));

        fieldset.appendChild(wrapper);
      } else {
        dropdown = wrapper.querySelector('[data-term-dropdown]');
      }

      const optionValues = boxedInputs.map((input) => input.value);
      const currentValues = Array.from(dropdown.options).map((option) => option.value);
      if (currentValues.join('|') !== optionValues.join('|')) {
        dropdown.innerHTML = '';
        boxedInputs.forEach((input) => {
          const option = document.createElement('option');
          option.value = input.value;
          option.textContent = input.value;
          dropdown.appendChild(option);
        });
      }

      const checked = boxedInputs.find((input) => input.checked);
      if (checked && dropdown.value !== checked.value) {
        dropdown.value = checked.value;
      }
    }

    applyGiftFields(isGift) {
      if (this.giftFields) {
        this.giftFields.style.display = isGift ? '' : 'none';

        // Positioned below the Term field, not left in its original
        // spot above the variant pickers. The Term fieldset lives
        // inside <variant-picker>, which the theme fully replaces on
        // every Card Quantity/Stamps/Term change (same reason
        // renderTermDropdown() rebuilds itself every call) - so this
        // has to re-insert on every call too, not just once, or the
        // fields would get silently orphaned along with whichever
        // <variant-picker> instance they'd been moved into the first
        // time. The previousElementSibling check makes that a no-op
        // once already correctly placed, so this doesn't thrash the
        // DOM on every poll tick.
        if (isGift) {
          const fieldset = this.termFieldset;
          if (fieldset && this.giftFields.previousElementSibling !== fieldset) {
            fieldset.insertAdjacentElement('afterend', this.giftFields);
          }
        }

        // theme.HTMLUpdateUtility.viewTransition() (assets/theme.js),
        // which swaps <variant-picker>'s contents on every option
        // change, dedupes ids by rewriting the `id`/`form` attribute of
        // every [id]/[form] element still inside the OLD picker node
        // right before discarding it - including these gift fields,
        // since insertAdjacentElement() above physically relocated them
        // inside <variant-picker>. That rewrite points `form` at a
        // throwaway id that no longer exists, silently dropping these
        // inputs from the real add-to-cart form's submission. Since the
        // fields themselves (and whatever a shopper already typed) are
        // never destroyed by that swap, restoring the real form id here
        // is enough to heal it - this runs on every applyState() call
        // (poll tick, variantChange, toggle change), so it's never
        // broken for longer than one 400ms tick.
        if (this.formId) {
          this.giftFields.querySelectorAll('[form]').forEach((field) => {
            if (field.getAttribute('form') !== this.formId) {
              field.setAttribute('form', this.formId);
            }
          });
        }
      }

      if (this.recipientInput) {
        if (isGift) {
          this.recipientInput.setAttribute('required', 'required');
        } else {
          this.recipientInput.removeAttribute('required');
          this.recipientInput.value = '';
        }
      }

      this.recipientAddressInputs.forEach((input) => {
        if (isGift) {
          input.setAttribute('required', 'required');
        } else {
          input.removeAttribute('required');
          input.value = '';
        }
      });

      if (this.recipientAddress2Input && !isGift) {
        this.recipientAddress2Input.value = '';
      }

      if (this.giftMessageInput && !isGift) {
        this.giftMessageInput.value = '';
      }
    }

    // Shown only in For Me mode (the merchant-authored note explains the
    // recurring charge, so it has nothing to say once A Gift has turned
    // that off), positioned above the quantity/Add to cart row rather
    // than left wherever it was originally rendered in the markup.
    applyRecurringNote(isGift) {
      if (!this.recurringNote) return;

      this.recurringNote.hidden = isGift;

      if (!isGift) {
        const wrapper = this.addToCartWrapper;
        if (wrapper && this.recurringNote.nextElementSibling !== wrapper) {
          wrapper.insertAdjacentElement('beforebegin', this.recurringNote);
        }
      }
    }

    // Mirror of applyRecurringNote() above, shown only in A Gift mode
    // (the one-time-purchase option) instead.
    applyNonRecurringNote(isGift) {
      if (!this.nonRecurringNote) return;

      this.nonRecurringNote.hidden = !isGift;

      if (isGift) {
        const wrapper = this.addToCartWrapper;
        if (wrapper && this.nonRecurringNote.nextElementSibling !== wrapper) {
          wrapper.insertAdjacentElement('beforebegin', this.nonRecurringNote);
        }
      }
    }

    applySubscriptionWidget(isGift) {
      const widget = this.subscriptionWidget;
      if (!widget) return;

      // Always hidden, in both modes - the widget exists purely to carry
      // the correct hidden selling_plan value with the order (or none,
      // for a gift). There's nothing left for a shopper to choose here:
      // For Me is locked to Monthly automatically, and A Gift never
      // subscribes, so the widget's own tab/panel UI would just be
      // confusing to show either way.
      widget.style.display = 'none';

      const sellingPlanInput = widget.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput) {
        sellingPlanInput.disabled = isGift;
      }

      if (!isGift) {
        this.forceSubscribeOnly(widget);
      }
    }

    // Applied to every per-variant <section> the widget has rendered so
    // far (not just the currently-visible one), so the OTP label is
    // already hidden whichever section the app switches into view when
    // Card Quantity/Stamps changes.
    //
    // The tab click and selling-plan click, though, are scoped to only
    // the currently ACTIVE section (the one without the app's own
    // shopify_subscriptions_app_block--hidden class) - a live capture
    // showed why: clicking an inactive section's tab still flips that
    // input's own `checked` property (a pure DOM side effect that
    // happens regardless of visibility), but the app itself only reacts
    // to - and clears the paired selling_plan radio's `disabled` for -
    // whichever section is actually active. Clicking every section up
    // front used to mark every tab as already-checked immediately, which
    // then permanently blocked ever clicking it again once it later
    // became the active one (our own idempotency check saw checked:true
    // and skipped it) - exactly why only the variant selected on page
    // load ever ended up with a real, enabled subscription.
    //
    // Two separate things need to happen, and they're two separate
    // radio groups in the widget's own markup:
    //  1. The visible tab switcher (name="tab_...", ids tab_otp_/
    //     tab_subscribe_) - purely cosmetic, decides which panel shows.
    //  2. The actual submitted purchase option (name="purchaseOption_
    //     ..."), which holds a one_time_purchase radio and a
    //     selling_plan radio - THIS is what checkout reads.
    // Switching tab 1 is what the app's own script reacts to by
    // clearing `disabled` on the selling_plan radio in group 2 (this is
    // what the manual-click test earlier in this project's history
    // showed), so both need to be driven from here, not just the tab.
    //
    // Both are clicked via their <label>, not their <input> directly.
    // The inputs are visually hidden (opacity:0; pointer-events:none)
    // and native label-click behavior forwards to the input's built-in
    // toggle either way, so clicking the label is a closer match to
    // what a real user interaction looks like than a raw input.click()
    // call - if the app's own interactive logic is bound to the label
    // (the only element real users can actually see/click), calling
    // .click() on the underlying input skips it entirely, even though
    // the input's own checked state still flips.
    forceSubscribeOnly(widget) {
      widget.querySelectorAll('.shopify_subscriptions_app_block').forEach((section) => {
        const otpTab = section.querySelector('input.tab_radio[id*="tab_otp_"]');
        const otpLabel = otpTab ? section.querySelector('label.tab_label[for="' + otpTab.id + '"]') : null;
        if (otpLabel) otpLabel.style.display = 'none';

        if (section.classList.contains('shopify_subscriptions_app_block--hidden')) return;

        const subscribeTab = section.querySelector('input.tab_radio[id*="tab_subscribe_"]');
        if (subscribeTab && !subscribeTab.checked) {
          const subscribeLabel = section.querySelector('label.tab_label[for="' + subscribeTab.id + '"]');
          (subscribeLabel || subscribeTab).click();
        }

        const sellingPlanRadio = section.querySelector('input[data-radio-type="selling_plan"]');
        if (sellingPlanRadio && !sellingPlanRadio.disabled && !sellingPlanRadio.checked) {
          const sellingPlanLabel = sellingPlanRadio.closest('label');
          (sellingPlanLabel || sellingPlanRadio).click();
        }
      });
    }
  }

  if (!customElements.get('subscription-gift-toggle')) {
    customElements.define('subscription-gift-toggle', SubscriptionGiftToggle);
  }
})();
